// Constants
import { DurationConstants } from "../../constants/GeneralConstants";

// This field stores the no of days used in backend for the impressions
// For example if the response has 300000 imp as a value
// Then imp per day = 300000/30
export const IMP_DAYS = DurationConstants.THIRTY_DAYS;

/**
 * This function updates the segment/stretch trace to be able to
 * display on the map..
 * @param {*} roadObject
 */
export function updateRoadTrace(roadObject = {}) {
  const { trace = [] } = roadObject || {};
  const newTrace = trace.map((eachCoordinate) => [
    eachCoordinate.latitude,
    eachCoordinate.longitude,
  ]);
  roadObject.trace = newTrace;
  return roadObject;
}

export function updateImpDetails(roadObject = {}) {
  const { otsAvg, otsLitAvg } = roadObject || {};
  roadObject.otsAvg = otsAvg ? otsAvg / IMP_DAYS : otsAvg;
  roadObject.otsLitAvg = otsLitAvg ? otsLitAvg / IMP_DAYS : otsLitAvg;
  roadObject.originalOtsAvg = otsAvg;
  roadObject.originalOtsLitAvg = otsLitAvg;
  return roadObject;
}

export function updateTgImpDetails(roadObj = {}) {
  const { targetOts, targetOtsLit, genericOts, genericOtsLit, ots, otsLit } =
    roadObj || {};
  roadObj.targetOts = targetOts ? targetOts / IMP_DAYS : targetOts;
  roadObj.targetOtsLit = targetOtsLit ? targetOtsLit / IMP_DAYS : targetOtsLit;
  roadObj.genericOts = genericOts ? genericOts / IMP_DAYS : genericOts;
  roadObj.genericOtsLit = genericOtsLit
    ? genericOtsLit / IMP_DAYS
    : genericOtsLit;
  roadObj.ots = ots ? ots / IMP_DAYS : ots;
  roadObj.otsLit = otsLit ? otsLit / IMP_DAYS : otsLit;

  return roadObj;
}

/**
 * Filtered City Stretches
 * based on applied filters,
 * return filtered stretches map
 */
export function getFilteredCityStretches(
  originalCityStretchesMap = {},
  zones = [],
  arterialRoutes = [],
  mediaTypes = []
) {
  // if no city stretches present
  if (Object.keys(originalCityStretchesMap).length === 0) {
    return {};
  }

  // checking any filter is applied
  const isZonesApplied = zones.length > 0;
  const isArterialRoutesApplied = arterialRoutes.length > 0;
  const isMediaTypesApplied = mediaTypes.length > 0;

  // is selected filter present
  const isSelectedFiltersPresent =
    isZonesApplied || isArterialRoutesApplied || isMediaTypesApplied;

  // if no filters present
  if (!isSelectedFiltersPresent) {
    return originalCityStretchesMap;
  }

  // filtered stretches map
  const filteredStretchesMap = {};

  // Generic filter helper
  function filterStretch(stretchId, stretchInfo) {
    const { zoneId, metaData = {} } = stretchInfo;
    const { routeType } = metaData;

    const matchZone = !isZonesApplied || zones.includes(zoneId);
    const matchRoute =
      !isArterialRoutesApplied || arterialRoutes.includes(routeType);

    return matchZone && matchRoute;
  }

  Object.entries(originalCityStretchesMap).forEach(
    ([stretchId, stretchInfo]) => {
      if (filterStretch(stretchId, stretchInfo)) {
        filteredStretchesMap[stretchId] = stretchInfo;
      }
    }
  );

  // is zone or routeType applied
  const isZoneOrRouteTypeApplied = isZonesApplied || isArterialRoutesApplied;
  const stretchesToFilterMedia = isZoneOrRouteTypeApplied
    ? filteredStretchesMap
    : originalCityStretchesMap;

  // if no mediaType filter is applied
  if (!isMediaTypesApplied) {
    return stretchesToFilterMedia;
  }

  // media filter stretches map
  const mediaFilteredMap = {};
  Object.entries(stretchesToFilterMedia).forEach(([stretchId, stretchInfo]) => {
    const filteredOrgMedias = (stretchInfo.orgMedias || []).filter(
      ({ mediaTag }) => mediaTypes.includes(mediaTag)
    );

    if (filteredOrgMedias.length === 0) {
      return;
    }

    mediaFilteredMap[stretchId] = {
      ...stretchInfo,
      orgMedias: filteredOrgMedias,
    };
  });

  return mediaFilteredMap;
}

// Touch Points Filtered Stretches Map
export function getFilteredStretchesForTouchPoints(
  originalCityStretchesMap = {},
  selectedTouchPoints = []
) {
  if (selectedTouchPoints.length === 0) {
    return {};
  }

  const touchPointsFilteredStretchesMap = {};
  Object.keys(originalCityStretchesMap).forEach((stretchId) => {
    const stretchInfo = originalCityStretchesMap[stretchId];
    const { metaData = {} } = stretchInfo || {};

    // stretch touch points
    const touchPoints = metaData?.touchPoints || [];

    // checking association
    const isSelectedTpAssociatedToStretch = touchPoints.some((tp) =>
      selectedTouchPoints.includes(tp)
    );

    if (isSelectedTpAssociatedToStretch) {
      touchPointsFilteredStretchesMap[stretchId] = stretchInfo;
    }
  });

  return touchPointsFilteredStretchesMap;
}

// FOL Filtered Stretches Map
export function getFilteredStretchesForFOL(
  originalCityStretchesMap = {},
  freqOptLocations = []
) {
  if (freqOptLocations.length === 0) {
    return {};
  }

  const folFilteredStretchesMap = {};

  Object.keys(originalCityStretchesMap).forEach((stretchId) => {
    const stretchInfo = originalCityStretchesMap[stretchId];
    const { metaData = {} } = stretchInfo || {};

    // stretch freqOptLocation
    const locationName = metaData?.locationName || "";

    // checking association
    const isSelectedFolAssociatedToStretch =
      freqOptLocations.includes(locationName) || false;

    if (isSelectedFolAssociatedToStretch) {
      folFilteredStretchesMap[stretchId] = stretchInfo;
    }
  });

  return folFilteredStretchesMap;
}

function getFilteredOptions(
  roadStretchesMap = {},
  zoneIds = [],
  mediaTags = [],
  routeTypes = [],
  hasNoFilters,
  appliedFrom
) {
  // if no filter applied
  if (hasNoFilters) {
    return {
      zoneIds: [],
      mediaTags: [],
      routeTypes: [],
    };
  }

  const matchedZoneIds = new Set();
  const matchedMediaTags = new Set();
  const matchedRouteTypes = new Set();

  Object.values(roadStretchesMap).forEach((stretch) => {
    const { zoneId, metaData, orgMedias } = stretch || {};
    const stretchMediaTags = orgMedias.map((media) => media.mediaTag);
    const stretchRouteType = metaData?.routeType || "";

    // If a filter is provided, the stretch must satisfy it
    const matchByZone = zoneIds.length === 0 || zoneIds.includes(zoneId);
    const matchByMedia =
      mediaTags.length === 0 ||
      stretchMediaTags.some((tag) => mediaTags.includes(tag));
    const matchByRoute =
      routeTypes.length === 0 || routeTypes.includes(stretchRouteType);

    if (appliedFrom === "zone") {
      if (matchByZone) {
        matchedZoneIds.add(zoneId);
        stretchMediaTags.forEach((tag) => matchedMediaTags.add(tag));
        matchedRouteTypes.add(stretchRouteType);
      }
    }

    if (appliedFrom === "mediaType") {
      if (matchByMedia) {
        matchedZoneIds.add(zoneId);
        stretchMediaTags.forEach((tag) => matchedMediaTags.add(tag));
        matchedRouteTypes.add(stretchRouteType);
      }
    }

    if (appliedFrom === "routeType") {
      if (matchByRoute) {
        matchedZoneIds.add(zoneId);
        stretchMediaTags.forEach((tag) => matchedMediaTags.add(tag));
        matchedRouteTypes.add(stretchRouteType);
      }
    }
  });

  const filteredOptions = {
    zoneIds: Array.from(matchedZoneIds),
    mediaTags: Array.from(matchedMediaTags),
    routeTypes: Array.from(matchedRouteTypes),
  };

  return filteredOptions;
}

// getting Applied Filter Order
function getAppliedFilterOrder(
  prevAppliedFilterOrder = {},
  appliedFrom,
  hasNoFilters,
  isZonesApplied,
  isMediaTypesApplied,
  isArterialRoutesApplied
) {
  let firstApplied = prevAppliedFilterOrder?.firstApplied || "";
  let secondApplied = prevAppliedFilterOrder?.secondApplied || "";
  let thirdApplied = prevAppliedFilterOrder?.thirdApplied || "";

  const appliedMap = {
    isZonesApplied,
    isMediaTypesApplied,
    isArterialRoutesApplied,
  };
  const appliedFilterCount = Object.keys(appliedMap).filter(
    (key) => appliedMap[key]
  ).length;

  if (hasNoFilters) {
    return { firstApplied: "", secondApplied: "", thirdApplied: "" };
  }

  if (!prevAppliedFilterOrder.firstApplied) {
    firstApplied = appliedFrom;
  } else if (!prevAppliedFilterOrder.secondApplied) {
    secondApplied = appliedFrom;
  } else if (!prevAppliedFilterOrder.thirdApplied) {
    thirdApplied = appliedFrom;
  }

  if (appliedFilterCount === 1) {
    secondApplied = "";
    thirdApplied = "";
  } else if (appliedFilterCount === 2) {
    thirdApplied = "";
  }

  if (
    (appliedFrom === "zone" &&
      prevAppliedFilterOrder.firstApplied === "zone" &&
      !isZonesApplied) ||
    (appliedFrom === "mediaType" &&
      prevAppliedFilterOrder.firstApplied === "mediaType" &&
      !isMediaTypesApplied) ||
    (appliedFrom === "routeType" &&
      prevAppliedFilterOrder.firstApplied === "routeType" &&
      !isArterialRoutesApplied)
  ) {
    firstApplied = "";
    secondApplied = "";
    thirdApplied = "";
  }

  if (
    (appliedFrom === "zone" &&
      prevAppliedFilterOrder.secondApplied === "zone" &&
      !isZonesApplied) ||
    (appliedFrom === "mediaType" &&
      prevAppliedFilterOrder.secondApplied === "mediaType" &&
      !isMediaTypesApplied) ||
    (appliedFrom === "routeType" &&
      prevAppliedFilterOrder.secondApplied === "routeType" &&
      !isArterialRoutesApplied)
  ) {
    secondApplied = "";
    thirdApplied = "";
  }

  return { firstApplied, secondApplied, thirdApplied };
}

// updating appliedFiltersDetailsMap with first, second, third applied filter options
function updateAppliedFilterMap(
  appliedFilterOrder = {},
  prevAppliedFiltersDetailsMap = {},
  appliedFiltersDetailsMap = {},
  filteredOptions = {},
  appliedFrom = ""
) {
  const { firstApplied, secondApplied, thirdApplied } =
    appliedFilterOrder || {};
  const {
    firstAppliedFilterOptions: prevFirst = {},
    secondAppliedFilterOptions: prevSecond = {},
    thirdAppliedFilterOptions: prevThird = {},
    appliedFrom: prevFrom = "",
  } = prevAppliedFiltersDetailsMap || {};

  const applyFilter = (level, isApplied, nextLevelApplied, prevOptions) => {
    const key = `${level}AppliedFilterOptions`;
    if (isApplied) {
      if (isApplied === appliedFrom) {
        appliedFiltersDetailsMap[key] = filteredOptions;
      } else if (nextLevelApplied) {
        appliedFiltersDetailsMap[key] = prevOptions;
      } else if (appliedFrom === prevFrom) {
        appliedFiltersDetailsMap[key] = filteredOptions;
      } else if (Object.keys(prevOptions).length > 0) {
        appliedFiltersDetailsMap[key] = prevOptions;
      } else {
        appliedFiltersDetailsMap[key] = filteredOptions;
      }
    } else {
      delete appliedFiltersDetailsMap[key];
    }
  };

  applyFilter("first", firstApplied, secondApplied, prevFirst);
  applyFilter("second", secondApplied, thirdApplied, prevSecond);
  applyFilter("third", thirdApplied, null, prevThird); // third is final level
}

/**
 * construct Applied Filters Details Map
 */
export function constructAppliedFiltersDetailsMap(
  roadStretchesMap = {},
  zones = [],
  freqOptLocations = [],
  arterialRoutes = [],
  mediaTypes = [],
  appliedFrom = "",
  prevAppliedFiltersDetailsMap = {}
) {
  // checking any filter is applied
  const isZonesApplied = zones.length > 0;
  const isFreqOptLocationsApplied = freqOptLocations.length > 0;
  const isArterialRoutesApplied = arterialRoutes.length > 0;
  const isMediaTypesApplied = mediaTypes.length > 0;

  const hasNoFilters =
    zones.length === 0 &&
    mediaTypes.length === 0 &&
    arterialRoutes.length === 0;

  // prev data
  const { appliedFilterOrder: prevAppliedFilterOrder = {} } =
    prevAppliedFiltersDetailsMap || {};

  // applied filter order
  const appliedFilterOrder = getAppliedFilterOrder(
    prevAppliedFilterOrder,
    appliedFrom,
    hasNoFilters,
    isZonesApplied,
    isMediaTypesApplied,
    isArterialRoutesApplied
  );

  // getting new filtered options for zones, mediaTypes, routeTypes
  const filteredOptions = getFilteredOptions(
    roadStretchesMap,
    zones,
    mediaTypes,
    arterialRoutes,
    hasNoFilters,
    appliedFrom
  );

  // map with additional keys
  const appliedFiltersDetailsMap = {
    zones,
    freqOptLocations,
    arterialRoutes,
    mediaTypes,
    isZonesApplied,
    isFreqOptLocationsApplied,
    isArterialRoutesApplied,
    isMediaTypesApplied,
    filteredOptions,
    isAnyFilterApplied: !hasNoFilters,
    appliedFilterOrder,
    appliedFrom,
  };

  // updating appliedFiltersDetailsMap with first, second, third applied filter options
  updateAppliedFilterMap(
    appliedFilterOrder,
    prevAppliedFiltersDetailsMap,
    appliedFiltersDetailsMap,
    filteredOptions,
    appliedFrom
  );

  return appliedFiltersDetailsMap;
}
